import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ListParticipant,
  listParticipantApiClient,
  ListParticipantRequest,
} from '@src/api/tenant/listParticipant/listParticipantApiClient';
import { PeriodRequest } from '@src/api/types/communityDashboard';
import { Pagination } from '@src/api/types/pagination';
import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useQueryParams } from '@src/hooks/useQueryParams';
import { getPresetByKey } from '@src/modules/constants/dateRangePreset';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import Title from '@src/ui/elements/typography/Title.tsx';
import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';

import DataTable, { Column } from '../community/component/DataTable';
import SummaryDateRangeSelector from '../community/component/SummaryDateRangeSelector/SummaryDateRangeSelector';
const DEFAULT_PRESET = '7' as const;
type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  preset: PeriodRequest | null;
};

const participantColumns: Column[] = [
  { title: '', key: 'id', width: 'w-[52px]', nowrap: true },
  {
    title: '企業名',
    key: 'company_name',
    width: 'w-[160px]',
    sortable: true,
    nowrap: true,
  },
  {
    title: '氏名',
    key: 'full_name',
    width: 'w-[120px]',
    sortable: true,
    nowrap: true,
  },
  {
    title: '参加チャンネル数',
    key: 'channels_count',
    width: 'w-[140px]',
    nowrap: true,
    sortable: true,
  },
  {
    title: '投稿数',
    key: 'posts',
    width: 'w-[80px]',
    sortable: true,
    nowrap: true,
  },
  {
    title: 'リアクション数',
    key: 'reactions',
    width: 'w-[120px]',
    sortable: true,
    nowrap: true,
  },
  {
    title: '総アクション数',
    key: 'total_actions',
    width: 'w-[120px]',
    sortable: true,
    nowrap: true,
  },
  {
    title: '最終アクセス日',
    key: 'last_accessed_at',
    width: 'w-[120px]',
    nowrap: true,
    sortable: true,
  },
];

const ListParticipantForm: React.FC = () => {
  const today = dayjs().toDate();
  const location = useLocation();
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [isCsvLoading, setIsCsvLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [isDateRangeOpen, setIsDateRangeOpen] = useState<boolean>(false);
  const [listParticipants, setListParticipants] = useState<ListParticipant[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: subDays(today, 7),
    endDate: subDays(today, 1),
    preset: DEFAULT_PRESET,
  });
  const [availableDateRange, setAvailableDateRange] = useState<{
    earliest: Date | null;
    latest: Date | null;
  }>({ earliest: null, latest: null });
  const [hasInitialized, setHasInitialized] = useState(false);

  const state = location.state as DateRange;

  const {
    searchValue,
    setSearchValue,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    sort,
    page,
    keyword,
    direction,
  } = useQueryParams({});

  useEffect(() => {
    if (!sort) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('sort', 'total_actions');
      newSearchParams.set('direction', 'desc');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [sort, searchParams, setSearchParams]);

  useEffect(() => {
    if (state && !hasInitialized) {
      setDateRange({
        startDate: state.startDate,
        endDate: state.endDate,
        preset: state.preset || DEFAULT_PRESET,
      });
      setIsDateRangeOpen(false);
      loadListParticipants(state);
      setHasInitialized(true);
    }
  }, [state, hasInitialized]);

  const handleDateRangeChange = async (range: {
    startDate: Date | null;
    endDate: Date | null;
    preset: PeriodRequest | null;
  }) => {
    if (!(range?.startDate && range?.endDate) && !range?.preset) {
      return;
    }
    setDateRange(range);
    await loadListParticipants(range);
  };

  const loadListParticipants = async (newRange?: DateRange) => {
    const rangeToUse = newRange || dateRange;
    const params: ListParticipantRequest = {
      period: rangeToUse.preset || 'custom',
      start_date: dayjs(rangeToUse.startDate).format('YYYY-MM-DD'),
      end_date: dayjs(rangeToUse.endDate).format('YYYY-MM-DD'),
      keyword: searchParams.get('keyword') || undefined,
      sort: searchParams.get('sort') || undefined,
      direction: (searchParams.get('direction') as 'asc' | 'desc' | undefined) || undefined,
      page: searchParams.get('page') || undefined,
    };
    setIsTableLoading(true);

    try {
      const res = await listParticipantApiClient.index(params);
      handleApiResponse(res, (data) => {
        setListParticipants(data.data || []);
        setPagination(data.pagination);
        setSearchedKeyword(params.keyword ?? '');
        setAvailableDateRange({
          earliest: data.available_date_range?.earliest
            ? dayjs(data.available_date_range.earliest).toDate()
            : null,
          latest: data.available_date_range?.latest
            ? dayjs(data.available_date_range.latest).toDate()
            : null,
        });

        const { type, start_date, end_date } = data.period || {};
        const validation = data.validation_messages ?? [];

        const isAll = type === 'all';
        const shouldOverwriteDate = isAll || validation.length > 0;

        if (shouldOverwriteDate) {
          const startDate =
            isAll && data.available_date_range?.earliest
              ? dayjs(data.available_date_range.earliest).toDate()
              : start_date
                ? dayjs(start_date).toDate()
                : null;

          const endDate =
            isAll && data.available_date_range?.latest
              ? dayjs(data.available_date_range.latest).toDate()
              : end_date
                ? dayjs(end_date).toDate()
                : null;

          setDateRange({
            preset: type as PeriodRequest,
            startDate,
            endDate,
          });
          setValidationMessages(validation);

          if (isAll) {
            setIsDateRangeOpen(false);
          }
        } else {
          setValidationMessages([]);
          setIsDateRangeOpen(false);
        }
      });
    } catch (error) {
      console.error('Failed to load participant activities:', error);
    } finally {
      setIsTableLoading(false);
    }
  };
  const formattedDateRangeText = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      return `${dayjs(dateRange.startDate).format('YYYY年MM月DD日')} - ${dayjs(dateRange.endDate).format('YYYY年MM月DD日')}`;
    }
    return '全期間（最古〜昨日）';
  }, [dateRange.startDate, dateRange.endDate]);

  const buttonLabel = useMemo(() => {
    return getPresetByKey(dateRange.preset).label;
  }, [dateRange.preset]);

  useEffect(() => {
    loadListParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, direction, page, keyword]);

  const handleTableSort = (key: string, direction: 'asc' | 'desc') => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('sort', key);
    newSearchParams.set('direction', direction);
    setSearchParams(newSearchParams);
  };

  const tableData = useMemo(() => {
    if (!pagination || typeof pagination.from !== 'number') {
      return [];
    }

    return listParticipants.map((participant, index) => {
      const rowData = Object.entries(participant).reduce(
        (acc, [key, value]) => {
          acc[key] = value === undefined ? null : value;
          return acc;
        },
        {} as Record<string, string | number | null>,
      );
      rowData['id'] = pagination.from + index;
      return rowData;
    });
  }, [listParticipants, pagination]);

  const handleClearSearch = () => {
    setSearchValue('');
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('keyword');
    setSearchParams(newSearchParams);
    setSearchedKeyword('');
  };

  const handleDownloadCSV = async () => {
    setIsCsvLoading(true);
    try {
      const params: ListParticipantRequest = {
        period: dateRange.preset || 'custom',
        start_date: dayjs(dateRange.startDate).format('YYYY-MM-DD'),
        end_date: dayjs(dateRange.endDate).format('YYYY-MM-DD'),
        keyword: searchParams.get('keyword') || undefined,
        sort: searchParams.get('sort') || undefined,
        direction: (searchParams.get('direction') as 'asc' | 'desc' | undefined) || undefined,
      };
      const response = await listParticipantApiClient.downloadCSV(params);
      handleApiResponse<Blob, Record<string, never>>(response, (csvBlob) => {
        const blobUrl = window.URL.createObjectURL(csvBlob);
        const anchorElement = document.createElement('a');
        anchorElement.href = blobUrl;
        const startDateStr = dayjs(dateRange.startDate).format('YYYYMMDD');
        const endDateStr = dayjs(dateRange.endDate).format('YYYYMMDD');
        const keyword = searchParams.get('keyword');
        const filenamePrefix = '参加者別アクティビティ一覧';
        const filenameSuffix = keyword ? `_${keyword}` : '';
        anchorElement.download = `${filenamePrefix}${filenameSuffix}_${startDateStr}-${endDateStr}.csv`;

        anchorElement.click();
        window.URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsCsvLoading(false);
    }
  };

  return (
    <main className=''>
      <SpinnerOverlay loading={isCsvLoading} />
      <div>
        <div className='flex flex-col'>
          <div className='flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>参加者別アクティビティ一覧</Title>
            </div>
            <div className='w-[250px]'>
              <InputSearch
                value={searchValue}
                defaultValue={keyword || ''}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={handleClearSearch}
              />
            </div>
          </div>
        </div>
        <div className='mt-4 flex justify-end'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-3'>
              <div className='text-xs'>{formattedDateRangeText}</div>
              <div className='relative'>
                <button
                  onClick={() => {
                    setIsDateRangeOpen((prev) => !prev);
                  }}
                  className='w-[190px] appearance-none rounded-[4px] border border-solid border-gray-250 bg-down bg-[center_right_12px] bg-no-repeat py-[10px] pl-4 pr-7 text-left text-sm font-medium'
                >
                  {buttonLabel}
                </button>
                {isDateRangeOpen && (
                  <div className={`absolute right-0 top-[50px] z-[9999]`}>
                    <SummaryDateRangeSelector
                      onChange={handleDateRangeChange}
                      onClose={() => setIsDateRangeOpen(false)}
                      initialStartDate={dateRange.startDate}
                      initialEndDate={dateRange.endDate}
                      initialPreset={dateRange.preset || DEFAULT_PRESET}
                      availableDateRange={availableDateRange}
                      errorMessages={validationMessages}
                    />
                  </div>
                )}
              </div>
            </div>
            <Button
              size='xs'
              onClick={handleDownloadCSV}
            >
              <SvgIconDownload
                color={brandColors['50']}
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </Button>
          </div>
        </div>
        {searchedKeyword && (
          <p className='overflow-wrap-anywhere mt-2 text-sm'>
            「{searchedKeyword}」で絞り込んだ結果：{pagination?.total ?? 0}件
          </p>
        )}
        <div className='relative min-h-[500px]'>
          <DataTable
            columns={participantColumns}
            data={tableData}
            marginTop='mt-4'
            padding='p-0'
            onSort={handleTableSort}
            sortKey={sort}
            sortDirection={direction as 'asc' | 'desc'}
            pagination={pagination}
            keyword={keyword ?? undefined}
            handlePageChange={handlePageChange}
          />
          {isTableLoading && (
            <div className='absolute inset-0 flex items-center justify-center bg-white/60'>
              <ClipLoader
                size={50}
                color={brandColors['500']}
                loading={isTableLoading}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
};
export default ListParticipantForm;
